import { sdk } from "@gc/ipecs-web-sdk";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { applySelectedDevices } from "../helpers/applySelectedDevices";
import { reportFailedCall } from "../helpers/reportFailedCall";
import { useDevices } from "./DevicesContext";
import { usePhoneControl } from "./PhoneControlContext";
import { useSoftphone } from "./SoftphoneContext";

const CallsContext = React.createContext();
const CallsProvider = ({ phoneControlMode, children }) => {
  const { call } = sdk;
  const { audioInput, audioOutput, videoInput, handleInitDevices } =
    useDevices();
  const [vmInfo, setVmInfo] = useState();

  const softphone = useSoftphone();
  const phoneControl = usePhoneControl();

  const getVmLink = async () => {
    return await call.getVoiceMailLink();
  };

  const handleVmCount = useCallback(() => {
    call.getVoiceMailCount().then((res) => setVmInfo(res));
  }, [call]);

  useEffect(() => handleVmCount(), [handleVmCount]);

  // setup event listeners
  useEffect(() => {
    call.onVMCountReceived((event) => {
      setVmInfo(event);
    });
  }, [call]);

  const hasActiveCall = () => {
    const activeCall = phoneControlMode
      ? phoneControl.activeCall
      : softphone.activeCall;
    return activeCall ? true : false;
  };

  // No caller awaits this (click-to-dial calls it and navigates away), so a
  // rejection escaping here is an unhandled one: silent, with the "calling"
  // state left on. Both modes catch their own dial failures and return the
  // promise so anything they miss lands here rather than nowhere.
  const handleMakeCall = async (destinationNumber, mediaType, delay = 0) => {
    try {
      if (phoneControlMode) {
        await phoneControl.handleMakeCall(destinationNumber, mediaType, delay);
      } else {
        await softphone.handleMakeCall(destinationNumber, mediaType, delay);
      }
    } catch (e) {
      console.error(e);
      reportFailedCall(e, destinationNumber, "calls-context");
      toast(`Failed to call | ${destinationNumber}`, { type: "error" });
    }
  };

  const handleUnparkCall = async (parkingLocation) => {
    if (phoneControlMode) {
      toast("Call parking not available in phone control mode", {
        type: "error",
      });
    } else {
      // check the devices selected are actually available
      await handleInitDevices();
      await applySelectedDevices(call, { audioInput, audioOutput, videoInput });

      softphone.handleUnparkCall(parkingLocation);
    }
  };

  const defaultContext = {
    hasActiveCall,
    handleMakeCall,
    handleUnparkCall,
    vmInfo,
    getVmLink,
  };

  return (
    <CallsContext.Provider value={defaultContext}>
      {children}
    </CallsContext.Provider>
  );
};

function useCalls() {
  return useContext(CallsContext);
}

export { CallsProvider, useCalls };
